import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, Observable } from 'rxjs';

const RESOLVED_LOCALE_STORAGE_KEY = 'fl-resolved-locale';

/**
 * Resolves, persists, and reactively broadcasts the locale used for date/number formatting
 * throughout the app.
 *
 * `LOCALE_ID` (still provided in app.module.ts for consistency, and for any third-party code that
 * reads it via DI) is resolved once per app session and frozen from then on - Angular's built-in
 * `DatePipe`/`DecimalPipe`/etc. don't react to it changing. Our own formatting pipes and components
 * don't rely on `LOCALE_ID` at all; they read `locale$`/`currentLocale` from this service instead,
 * which updates live whenever `UserService.setLanguage()` resolves a new language - or, for QA,
 * whenever the debug page's locale toggle calls `setLocale()` directly. See FA-6789.
 *
 * Lives in its own small library (rather than `@libs/shared/core`) so that anything needing just
 * this class - notably tests - doesn't have to pull in `@libs/shared/core`'s much larger transitive
 * dependency graph (akita queries, etc.) via its barrel.
 */
@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  // Overwritten by `resolve()` before any component reads it - see the app-bootstrap wiring in
  // app.module.ts. This value is never used as-is in practice.
  private readonly localeSubject = new BehaviorSubject<string>('de');

  public get locale$(): Observable<string> {
    return this.localeSubject.asObservable();
  }

  public get currentLocale(): string {
    return this.localeSubject.value;
  }

  public async resolve(supportedLanguages: string[], defaultLanguage: string): Promise<void> {
    const stored = (await Preferences.get({ key: RESOLVED_LOCALE_STORAGE_KEY })).value;

    if (stored && supportedLanguages.includes(stored)) {
      this.localeSubject.next(stored);

      return;
    }

    const deviceLanguage = (await Device.getLanguageTag()).value.split('-')[0] as string | undefined;

    this.localeSubject.next(
      deviceLanguage && supportedLanguages.includes(deviceLanguage) ? deviceLanguage : defaultLanguage,
    );
  }

  /** Updates the live locale immediately. Does not persist - see `persist()`. */
  public setLocale(locale: string): void {
    this.localeSubject.next(locale);
  }

  /**
   * Persists the resolved language so the next app cold start can pick it up for `LOCALE_ID`
   * without waiting on PouchDB sync. This is a separate concern from `setLocale()`: a debug-page
   * locale preview should update the live formatting without overwriting what the user actually
   * configured.
   */
  public async persist(language: string): Promise<void> {
    await Preferences.set({ key: RESOLVED_LOCALE_STORAGE_KEY, value: language });
  }
}
